import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useActiveProfile } from '../contexts/ActiveProfileContext'
import { API_BASE_URL } from '../config/api'

export const useAnalytics = () => {
  const location = useLocation()
  const { currentUser } = useAuth()
  const { activeProfile } = useActiveProfile()
  
  const prevPathRef = useRef<string | null>(null)

  useEffect(() => {
    const trackPageView = async () => {
      const currentPath = location.pathname + location.search
      
      // Avoid duplicate tracking of same path
      if (currentPath === prevPathRef.current) return
      
      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        }

        if (currentUser) {
          // Get fresh token
          const token = await currentUser.getIdToken()
          headers['Authorization'] = `Bearer ${token}`
        }

        if (activeProfile?.profileId) {
          headers['X-Profile-ID'] = activeProfile.profileId
        }

        // Extract metadata for spotlight pages
        const metadata: Record<string, any> = {
          search: location.search,
          hash: location.hash
        }

        // If on a spotlight page, extract coach_id and spotlight_slug
        const spotlightMatch = location.pathname.match(/^\/spotlight\/([^/]+)$/)
        if (spotlightMatch) {
          const slug = spotlightMatch[1]
          metadata.spotlight_slug = slug
          
          // Extract source from URL params
          const params = new URLSearchParams(location.search)
          if (params.get('source')) {
            metadata.source = params.get('source')
          }
          
          // Fetch coach_id from the spotlight page (if we can)
          // For now, we'll let the backend analytics service correlate this
          // by querying the coach_spotlight_interviews collection
          // Alternatively, we could fetch the interview data here, but that adds latency
        }

        const payload = {
            event: 'page_view',
            path: currentPath,
            referrer: prevPathRef.current || document.referrer || null,
            metadata
        }
        
        await fetch(`${API_BASE_URL}/api/v1/analytics/event`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        })

      } catch (error) {
        // Silent fail for analytics
        // eslint-disable-next-line no-console
        console.debug('Analytics tracking failed', error)
      } finally {
        prevPathRef.current = currentPath
      }
    }

    trackPageView()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, currentUser?.uid, activeProfile?.profileId])
}


